import React from 'react';
import {Link, NavLink} from 'react-router-dom';

import Authenticated from 'Components/Layouts/Authenticated';
import {Loading, ActionsButton} from 'Components/Partials';
import {ArchiveUser} from 'Components/HumanResources/AdminComponents';

import {Toast} from 'Services';
import UsersApi from 'Services/Api/Admin/HumanResources/Users';
import AdminTimesheetsApi from 'Services/Api/Admin/HumanResources/Timesheets';
import RecordApi from 'Services/Api/Cms/Elements/Record';

import {formatDate, joinNames, Pill} from './Ui';

/**
 * The frame around every tab of a staff member's folder: their name and services at the top,
 * and the tabs (Overview, Holiday, HR details, Forms, Rates, Timesheets).
 *
 * Children is a function given {user, overview, reload}.
 */
export default class FolderLayout extends React.Component {
    /**
     * @var state
     */
    state = {
        user: null,
        overview: null,
        loading: true,
    };

    /**
     * @method componentDidMount
     */
    componentDidMount = () => {
        this.load();

        // Approving leave or timesheets, or filling in a form, changes the badges and figures.
        AdminTimesheetsApi.on('updated', this.reload);
        RecordApi.on('updated', this.reload);
    };

    /**
     * @method componentDidUpdate
     * @param {object} prevProps
     */
    componentDidUpdate = (prevProps) => {
        if (prevProps.userId !== this.props.userId) {
            this.load();
        }
    };

    /**
     * @method componentWillUnmount
     */
    componentWillUnmount = () => {
        AdminTimesheetsApi.removeListener('updated', this.reload);
        RecordApi.removeListener('updated', this.reload);
    };

    /**
     * @method load
     */
    load = async () => {
        this.setState({loading: true});
        await this.reload();
        this.setState({loading: false});
    };

    /**
     * @method reload
     */
    reload = async () => {
        const {userId} = this.props;

        const [user, overview] = await Promise.all([
            UsersApi.get({user: userId}),
            UsersApi.getFolderOverview(userId),
        ]);

        if (!user.success || !overview.success) {
            return Toast.error();
        }

        // Keep the old HR pages (which read this cache) in step with the folder.
        localStorage.setItem('_user', JSON.stringify(user.data.data));

        this.setState({user: user.data.data, overview: overview.data.data});
    };

    /**
     * @method tabs
     * @return {array}
     */
    tabs = () => {
        const {userId} = this.props;
        const {overview} = this.state;
        const base = `/admin/human-resources/staff/${userId}`;

        const toReview = (overview?.timesheets?.to_approve ?? 0) + (overview?.timesheets?.queried ?? 0);

        return [
            {key: 'overview', label: 'Overview', to: base},
            {key: 'holiday', label: 'Holiday', to: `${base}/holiday`, badge: overview?.holiday?.pending_requests, tone: 'warn'},
            {key: 'hr-details', label: 'HR details', to: `${base}/hr-details`},
            {key: 'forms', label: 'Forms', to: `${base}/forms`, badge: overview?.forms?.outstanding?.length, tone: 'bad'},
            window.base.features.rates ? {key: 'rates', label: 'Rates', to: `${base}/rates`} : null,
            {key: 'timesheets', label: 'Timesheets', to: `${base}/timesheets`, badge: toReview, tone: 'warn'},
        ].filter(Boolean);
    };

    /**
     * @method moreActions
     * @return {array}
     */
    moreActions = () => {
        const {user} = this.state;

        return [
            {type: 'link', to: `/admin/users/store/${user.id}`, text: 'Edit login and group'},
            {type: 'link', to: `/admin/human-resources/calendar/${user.id}`, text: 'Calendar'},
            {type: 'link', to: `/admin/human-resources/documents/${user.id}`, text: 'Documents'},
            !user.is_archived ? {type: 'modalTrigger', component: ArchiveUser, props: {userId: user.id}, text: 'Archive'} : null,
        ].filter(Boolean);
    };

    /**
     * @method renderHero
     * @return {JSX.Element}
     */
    renderHero = () => {
        const {user, overview} = this.state;
        const hr = overview?.hr ?? {};

        const subtitle = [
            hr.job_title,
            hr.services?.length ? joinNames(hr.services) : null,
            hr.start_date ? 'Started ' + formatDate(hr.start_date) : null,
        ].filter(Boolean).join(' · ');

        return (
            <div className="bg-app-supporting-grey">
                <div className="bg-app-supporting-yellow pl-16 md:pl-8 pr-4 md:pr-8 py-5 flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <div
                            aria-hidden="true"
                            className="flex-none w-16 h-16 rounded-full bg-white flex items-center justify-center font-league-spartan font-bold text-2xl text-app-leading"
                        >
                            {user ? (user.first_name?.[0] ?? '') + (user.last_name?.[0] ?? '') : ''}
                        </div>
                        <div>
                            <Link to="/admin/human-resources" className="text-sm font-bold text-app-typography hover:underline">
                                ← All staff
                            </Link>
                            <h1 className="font-league-spartan font-bold text-3xl leading-tight text-app-typography">
                                {user?.full_name ?? 'Loading...'}
                            </h1>
                            {subtitle && <div className="text-app-typography">{subtitle}</div>}
                        </div>
                    </div>

                    {user && (
                        <div className="flex flex-wrap items-center gap-3">
                            <span className={`bg-white font-bold text-sm px-3 py-1 rounded-full ${user.is_archived ? 'text-red-800' : 'text-green-800'}`}>
                                {user.is_archived ? 'Archived' : 'Active'}
                            </span>
                            <ActionsButton buttons={this.moreActions()} />
                        </div>
                    )}
                </div>

                <nav aria-label="Staff folder sections" className="bg-white border-b border-gray-200 pl-14 md:pl-6 pr-4 flex gap-1 overflow-x-auto">
                    {this.tabs().map(tab => (
                        <NavLink
                            key={tab.key}
                            to={tab.to}
                            exact
                            className="px-3 pt-4 pb-3 border-b-4 border-transparent font-bold text-gray-600 whitespace-nowrap hover:text-app-typography"
                            activeClassName="!border-app-leading !text-app-typography"
                        >
                            {tab.label}
                            {tab.badge > 0 && <span className="ml-2"><Pill tone={tab.tone}>{tab.badge}</Pill></span>}
                        </NavLink>
                    ))}
                </nav>
            </div>
        );
    };

    /**
     * @method render
     * @return {JSX.Element}
     */
    render() {
        const {children} = this.props;
        const {user, overview, loading} = this.state;

        return (
            <Authenticated hero={this.renderHero()}>
                <div className="max-w-6xl">
                    {loading && <Loading/>}

                    {!loading && user && overview && children({user, overview, reload: this.reload})}
                </div>
            </Authenticated>
        );
    }
}
